import { RequireWhenAnyOf, RequireWhenOperator, RequireWhenRule } from '../types'

export const coerceNumber = (v: unknown): number | undefined => {
  if (typeof v === 'number' && !Number.isNaN(v)) return v
  if (typeof v === 'string') {
    const trimmed = v.trim()
    if (trimmed === '') return undefined
    const n = Number(trimmed)
    if (!Number.isNaN(n)) return n
  }
  return undefined
}

export const coerceBoolean = (v: unknown): boolean | undefined => {
  if (typeof v === 'boolean') return v
  if (typeof v === 'string') {
    const t = v.trim().toLowerCase()
    if (t === 'true') return true
    if (t === 'false') return false
  }
  if (typeof v === 'number') {
    if (v === 1) return true
    if (v === 0) return false
  }
  return undefined
}

export const equalsWithCoercion = (lhs: unknown, rhs: unknown): boolean => {
  const rn = coerceNumber(rhs)
  const ln = coerceNumber(lhs)
  if (rn !== undefined && ln !== undefined) return ln === rn

  const rb = coerceBoolean(rhs)
  const lb = coerceBoolean(lhs)
  if (rb !== undefined && lb !== undefined) return lb === rb

  return lhs === rhs
}

export const evalOp = (lhs: unknown, op: RequireWhenOperator, rhs: unknown): boolean => {
  switch (op) {
    case 'lt': {
      const l = coerceNumber(lhs)
      const r = coerceNumber(rhs)
      return l !== undefined && r !== undefined && l < r
    }
    case 'lte': {
      const l = coerceNumber(lhs)
      const r = coerceNumber(rhs)
      return l !== undefined && r !== undefined && l <= r
    }
    case 'gt': {
      const l = coerceNumber(lhs)
      const r = coerceNumber(rhs)
      return l !== undefined && r !== undefined && l > r
    }
    case 'gte': {
      const l = coerceNumber(lhs)
      const r = coerceNumber(rhs)
      return l !== undefined && r !== undefined && l >= r
    }
    case 'eq':
      return equalsWithCoercion(lhs, rhs)
    case 'neq':
      return !equalsWithCoercion(lhs, rhs)
    case 'in':
      return Array.isArray(rhs) && (rhs as unknown[]).some((v) => equalsWithCoercion(lhs, v))
    case 'notIn':
      return Array.isArray(rhs) && !(rhs as unknown[]).some((v) => equalsWithCoercion(lhs, v))
    case 'contains':
      if (Array.isArray(lhs)) {
        return lhs.some((item) => equalsWithCoercion(item, rhs))
      }
      return typeof lhs === 'string' && typeof rhs === 'string' && lhs.includes(rhs)
    case 'startsWith':
      return typeof lhs === 'string' && typeof rhs === 'string' && lhs.startsWith(rhs)
    case 'endsWith':
      return typeof lhs === 'string' && typeof rhs === 'string' && lhs.endsWith(rhs)
    case 'regex':
      try {
        const re = new RegExp(String(rhs))
        return typeof lhs === 'string' && re.test(lhs)
      } catch {
        return false
      }
    default:
      return false
  }
}

export const evalGroup = (
  data: Record<string, unknown>,
  group?: RequireWhenAnyOf
): { matched: boolean; matchedKey?: string } => {
  if (!group) return { matched: false }
  for (const [key, cond] of Object.entries(group)) {
    const op = (cond as { operator: RequireWhenOperator }).operator
    const value = (cond as { value: unknown }).value
    const passed = evalOp((data as Record<string, unknown>)[key], op, value)
    if (passed) return { matched: true, matchedKey: key }
  }
  return { matched: false }
}

/**
 * Evaluate whether a rule (with anyOf/allOf groups) is triggered given the current form data.
 */
export const evaluateRule = (data: Record<string, unknown>, rule: RequireWhenRule): boolean => {
  const anyOfResult = evalGroup(data, rule.anyOf as RequireWhenAnyOf | undefined)

  const allOf = rule.allOf as RequireWhenAnyOf | undefined
  const allOfResult = allOf
    ? Object.entries(allOf).every(([key, cond]) =>
        evalOp(
          data[key],
          (cond as { operator: RequireWhenOperator }).operator,
          (cond as { value: unknown }).value
        )
      )
    : false

  return anyOfResult.matched || allOfResult
}
